import type {
	API_AddSupportTicketCommentRequest,
	SupportTicketComment,
	SupportTicketStatus,
	SupportTicketType,
} from "@pilatestime/shared-types";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
	ArrowLeft,
	Calendar,
	CheckCircle,
	MessageSquare,
	RefreshCw,
	Send,
	Ticket,
	User,
	XCircle,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
	fetchUserSupportTicketByIdQueryOptions,
	useAddSupportTicketComment,
	useCancelSupportTicket,
	useReopenSupportTicket,
	useResolveSupportTicket,
} from "@/api/supportTickets";
import { ConfirmationWarningModal } from "@/components/ConfirmationWarningModal";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useApiParameters } from "@/hooks/useApiParameters";
import { useDateFormatter } from "@/hooks/useDateFormatter";

export const Route = createFileRoute(
	"/_auth/app/_app/help/support/tickets/$ticketId",
)({
	loader: async ({ context, params: { ticketId } }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		if (!token) throw new Error("No authentication token");

		context.queryClient.ensureQueryData(
			fetchUserSupportTicketByIdQueryOptions(token, ticketId),
		);

		return {
			userId,
			token,
			ticketId,
		};
	},
	component: SupportTicketDetailPage,
});

function SupportTicketDetailPage() {
	const { t } = useTranslation();
	const { formatDate, formatDateTime } = useDateFormatter();
	const { token, ticketId } = Route.useLoaderData();

	// Fetch ticket data
	const { data: ticket } = useSuspenseQuery(
		fetchUserSupportTicketByIdQueryOptions(token, ticketId),
	);

	// Comment form state
	const [commentContent, setCommentContent] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);

	// Status action modal state
	const [showCancelModal, setShowCancelModal] = useState(false);
	const [showResolveModal, setShowResolveModal] = useState(false);

	// Mutation for adding comments
	const addCommentMutation = useAddSupportTicketComment();

	// Mutations for status changes
	const cancelMutation = useCancelSupportTicket();
	const resolveMutation = useResolveSupportTicket();
	const reopenMutation = useReopenSupportTicket();

	// Check if ticket can be modified (not closed or resolved)
	const canModifyTicket =
		ticket.status !== "closed" && ticket.status !== "resolved";

	// Handle cancel ticket
	const handleCancelTicket = async () => {
		await cancelMutation.mutateAsync({ token, ticketId });
		setShowCancelModal(false);
	};

	// Handle resolve ticket
	const handleResolveTicket = async () => {
		await resolveMutation.mutateAsync({ token, ticketId });
		setShowResolveModal(false);
	};

	// Handle reopen ticket
	const handleReopenTicket = async () => {
		await reopenMutation.mutateAsync({ token, ticketId });
	};

	// Status badge component
	const getStatusBadge = (status: SupportTicketStatus) => {
		const statusConfig = {
			open: { color: "bg-blue-100 text-blue-800", label: t("help.statusOpen") },
			waiting_for_user: {
				color: "bg-yellow-100 text-yellow-800",
				label: t("help.statusWaitingForUser"),
			},
			waiting_for_staff: {
				color: "bg-orange-100 text-orange-800",
				label: t("help.statusWaitingForStaff"),
			},
			resolved: {
				color: "bg-green-100 text-green-800",
				label: t("help.statusResolved"),
			},
			closed: {
				color: "bg-gray-100 text-gray-800",
				label: t("help.statusClosed"),
			},
		};

		const config = statusConfig[status];
		return (
			<span
				className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}
			>
				{config.label}
			</span>
		);
	};

	// Type badge component
	const getTypeBadge = (type: SupportTicketType) => {
		const typeConfig = {
			request: {
				color: "bg-blue-100 text-blue-800",
				label: t("help.supportTypeRequest"),
			},
			suggestion: {
				color: "bg-purple-100 text-purple-800",
				label: t("help.supportTypeSuggestion"),
			},
			complaint: {
				color: "bg-red-100 text-red-800",
				label: t("help.supportTypeComplaint"),
			},
		};

		const config = typeConfig[type];
		return (
			<span
				className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}
			>
				{config.label}
			</span>
		);
	};

	// Handle comment submission
	const handleCommentSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!commentContent.trim()) return;

		setIsSubmitting(true);

		try {
			// Get token from the query key
			const commentData: API_AddSupportTicketCommentRequest = {
				content: commentContent.trim(),
			};

			await addCommentMutation.mutateAsync({
				token,
				ticketId,
				data: commentData,
			});

			// Reset form
			setCommentContent("");
		} catch (error) {
			console.error("Error adding comment:", error);
		} finally {
			setIsSubmitting(false);
		}
	};

	// Helper to get comment author display name
	const getCommentAuthorName = (comment: SupportTicketComment): string => {
		if (comment.isStaff) {
			return t("help.supportTeam");
		}
		// User comment - show "You" for the ticket owner
		return t("help.you");
	};

	// Render comment component
	const renderComment = (comment: SupportTicketComment) => {
		const authorName = getCommentAuthorName(comment);
		const isStaffComment = comment.isStaff;

		return (
			<div
				key={comment.id}
				className={`flex space-x-3 p-4 border-b border-base-200 last:border-b-0 ${
					isStaffComment ? "bg-primary/5" : ""
				}`}
			>
				<div className="flex-shrink-0">
					<div
						className={`w-8 h-8 rounded-full flex items-center justify-center ${
							isStaffComment ? "bg-primary/20" : "bg-base-300"
						}`}
					>
						<User
							className={`w-4 h-4 ${
								isStaffComment ? "text-primary" : "text-base-content/60"
							}`}
						/>
					</div>
				</div>
				<div className="flex-1 min-w-0">
					<div className="flex items-center space-x-2 mb-1">
						<span className="text-sm font-medium text-base-content">
							{authorName}
						</span>
						{isStaffComment && (
							<span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary">
								{t("help.staff")}
							</span>
						)}
						<span className="text-xs text-base-content/60">
							{formatDateTime(new Date(comment.createdAt).getTime())}
						</span>
					</div>
					<div className="text-sm text-base-content whitespace-pre-wrap">
						{comment.content}
					</div>
				</div>
			</div>
		);
	};

	return (
		<div className="h-full flex flex-col overflow-auto">
			{/* Header */}
			<div className="flex-shrink-0 p-6 border-b border-base-300">
				<div className="flex items-center justify-between">
					<div className="flex items-center space-x-4">
						<Link
							to="/app/help/support/tickets"
							className="btn btn-ghost btn-sm"
						>
							<ArrowLeft className="h-4 w-4" />
							{t("common.back")}
						</Link>
						<div>
							<h1 className="text-2xl font-bold text-base-content">
								{ticket.title}
							</h1>
							<p className="text-base-content/70">
								{t("help.ticketDetails")} #{ticket.id.slice(-8)}
							</p>
						</div>
					</div>
					<div className="flex items-center space-x-2">
						{getTypeBadge(ticket.type)}
						{getStatusBadge(ticket.status)}
					</div>
				</div>

				{/* Action Buttons */}
				<div className="flex items-center space-x-2 mt-4">
					{canModifyTicket && (
						<>
							<Button
								variant="outline"
								size="sm"
								onClick={() => setShowResolveModal(true)}
								disabled={resolveMutation.isPending}
								className="flex items-center space-x-1"
							>
								<CheckCircle className="h-4 w-4" />
								<span>{t("help.resolveTicket")}</span>
							</Button>
							<Button
								variant="outline"
								size="sm"
								onClick={() => setShowCancelModal(true)}
								disabled={cancelMutation.isPending}
								className="flex items-center space-x-1 text-error hover:text-error"
							>
								<XCircle className="h-4 w-4" />
								<span>{t("help.cancelTicket")}</span>
							</Button>
						</>
					)}
					{(ticket.status === "closed" || ticket.status === "resolved") && (
						<Button
							variant="outline"
							size="sm"
							onClick={handleReopenTicket}
							disabled={reopenMutation.isPending}
							className="flex items-center space-x-1"
						>
							<RefreshCw className="h-4 w-4" />
							<span>{t("help.reopenTicket")}</span>
						</Button>
					)}
				</div>
			</div>

			{/* Content */}
			<div className="flex-1 min-h-0 p-6 space-y-6">
				{/* Ticket Details Card */}
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center space-x-2">
							<Ticket className="h-5 w-5" />
							<span>{t("help.ticketInformation")}</span>
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div>
								<Label className="text-sm font-medium text-base-content/70">
									{t("help.supportFieldType")}
								</Label>
								<div className="mt-1">{getTypeBadge(ticket.type)}</div>
							</div>
							<div>
								<Label className="text-sm font-medium text-base-content/70">
									{t("common.status")}
								</Label>
								<div className="mt-1">{getStatusBadge(ticket.status)}</div>
							</div>
							<div>
								<Label className="text-sm font-medium text-base-content/70">
									{t("common.createdAt")}
								</Label>
								<div className="mt-1 flex items-center space-x-2">
									<Calendar className="h-4 w-4 text-base-content/60" />
									<span className="text-sm">
										{formatDate(new Date(ticket.createdAt).getTime())}
									</span>
								</div>
							</div>
							{ticket.updatedAt !== ticket.createdAt && (
								<div>
									<Label className="text-sm font-medium text-base-content/70">
										{t("common.updatedAt")}
									</Label>
									<div className="mt-1 flex items-center space-x-2">
										<Calendar className="h-4 w-4 text-base-content/60" />
										<span className="text-sm">
											{formatDate(new Date(ticket.updatedAt).getTime())}
										</span>
									</div>
								</div>
							)}
						</div>
						<div>
							<Label className="text-sm font-medium text-base-content/70">
								{t("help.supportFieldDescription")}
							</Label>
							<div className="mt-2 p-3 bg-base-100 rounded-lg border">
								<p className="text-sm text-base-content whitespace-pre-wrap">
									{ticket.description}
								</p>
							</div>
						</div>
					</CardContent>
				</Card>

				{/* Comments Section */}
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center space-x-2">
							<MessageSquare className="h-5 w-5" />
							<span>
								{t("help.comments")} ({ticket.comments.length})
							</span>
						</CardTitle>
					</CardHeader>
					<CardContent className="p-0">
						{/* Comments List */}
						{ticket.comments.length === 0 ? (
							<div className="p-6 text-center">
								<MessageSquare className="h-12 w-12 text-base-content/30 mx-auto mb-4" />
								<p className="text-base-content/70">{t("help.noComments")}</p>
							</div>
						) : (
							<div className="max-h-96 overflow-y-auto">
								{ticket.comments.map((comment) => renderComment(comment))}
							</div>
						)}

						{/* Add Comment Form */}
						<div className="border-t border-base-200 p-4">
							<form onSubmit={handleCommentSubmit} className="space-y-4">
								<div>
									<Label htmlFor="comment" className="text-sm font-medium">
										{t("help.addComment")}
									</Label>
									<Textarea
										id="comment"
										value={commentContent}
										onChange={(e) => setCommentContent(e.target.value)}
										placeholder={t("help.commentPlaceholder")}
										rows={3}
										className="mt-1"
										disabled={isSubmitting}
										maxLength={2000}
									/>
									<div className="flex justify-between items-center mt-2">
										<span className="text-xs text-base-content/50">
											{commentContent.length}/2000
										</span>
									</div>
								</div>
								<div className="flex justify-end">
									<Button
										type="submit"
										disabled={!commentContent.trim() || isSubmitting}
										className="flex items-center space-x-2 text-primary-content"
									>
										{isSubmitting ? (
											<>
												<div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
												<span>{t("help.commentSubmitting")}</span>
											</>
										) : (
											<>
												<Send className="h-4 w-4" />
												<span>{t("help.addComment")}</span>
											</>
										)}
									</Button>
								</div>
							</form>
						</div>
					</CardContent>
				</Card>
			</div>

			{/* Cancel Ticket Confirmation Modal */}
			<ConfirmationWarningModal
				isOpen={showCancelModal}
				onClose={() => setShowCancelModal(false)}
				onConfirm={handleCancelTicket}
				title={t("help.cancelTicketConfirmTitle")}
				subtitle={t("help.cancelTicketConfirmDescription")}
				confirmButtonText={t("help.cancelTicket")}
				loadingText={t("help.ticketCanceling")}
				isLoading={cancelMutation.isPending}
				variant="danger"
			/>

			{/* Resolve Ticket Confirmation Modal */}
			<ConfirmationWarningModal
				isOpen={showResolveModal}
				onClose={() => setShowResolveModal(false)}
				onConfirm={handleResolveTicket}
				title={t("help.resolveTicketConfirmTitle")}
				subtitle={t("help.resolveTicketConfirmDescription")}
				confirmButtonText={t("help.resolveTicket")}
				loadingText={t("help.ticketResolving")}
				isLoading={resolveMutation.isPending}
				variant="warning"
			/>
		</div>
	);
}
